// src/components/profile/ActivitySection.jsx
import React from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'

const ActivitySection = ({ userData, activeTab, setActiveTab }) => {
  const TABS = ['Posts', 'Interpretations', 'Comments', 'Blogs']
  
  // Filter activity data based on tab
  const getActivityData = () => {
    const rawData = userData?.activity?.[activeTab.toLowerCase()] || []
    
    // For comments, filter out interpretation comments and reply comments
    if (activeTab === 'Comments') {
      return rawData.filter(item => 
        !item.interpretation_id && // Exclude interpretation comments
        !item.parent_id && // Exclude reply comments (nested)
        (item.post_id || item.blog_id) // Only include post or blog comments
      )
    }
    
    return rawData
  }
  
  const activityData = getActivityData()

  const renderItem = (item) => {
    switch (activeTab) {
      case 'Posts':
        return (
          <Link
            to={`/post/${item.id}`}
            className="text-lg font-medium text-primary hover:underline"
          >
            {item.title || `Untitled post #${item.id}`}
          </Link>
        )

      case 'Comments':
        return (
          <div>
            <p className="text-gray-200">{item.content}</p>
            {/* Conditional link based on whether it's a post or blog comment */}
            {item.post_id && (
              <Link
                to={`/post/${item.post_id}`}
                className="text-sm text-blue-400 hover:underline"
              >
                {item.post_title || `View on post`}
              </Link>
            )}
            {item.blog_id && (
              <Link
                to={`/blog/${item.blog_id}`}
                className="text-sm text-blue-400 hover:underline"
              >
                {item.blog_title || `View on blog`}
              </Link>
            )}
          </div>
        )

      case 'Interpretations':
        return (
          <div>
            <p className="text-gray-200">{item.text}</p>
            <Link
              to={`/post/${item.post_id}`}
              className="text-sm text-blue-400 hover:underline"
            >
              {item.post_title || `View interpretation`}
            </Link>
          </div>
        )

      case 'Blogs':
        return (
          <Link
            to={`/blog/${item.id}`}
            className="text-lg font-medium text-primary hover:underline"
          >
            {item.title || `Untitled blog #${item.id}`}
          </Link>
        )

      default:
        return null
    }
  }

  return (
    <div className="mt-10">
      {/* Tab buttons */}
      <div className="border-b border-gray-800 mb-6">
        <div className="flex overflow-x-auto hide-scrollbar">
          {TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-3 px-4 font-medium transition-all duration-200 relative ${
                activeTab === tab ? 'text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              {tab}
              {activeTab === tab && (
                <motion.div
                  layoutId="activeTabIndicator"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                  initial={false}
                />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Activity display */}
      <div>
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {activityData.length > 0 ? (
              <div className="space-y-4">
                {activityData.map((item, idx) => (
                  <motion.div
                    key={item.id || idx}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: idx * 0.05 }}
                    whileHover={{ y: -3 }}
                    className="bg-dark-gray/30 backdrop-blur-sm border border-gray-800 rounded-xl p-5 hover:border-gray-700 transition-all duration-300 shadow-md"
                  >
                    {renderItem(item)}
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="bg-dark-gray/30 backdrop-blur-sm border border-gray-800 rounded-xl p-8 text-center">
                <h3 className="text-lg font-medium text-gray-400 mb-1">
                  No {activeTab.toLowerCase()} yet
                </h3>
                <p className="text-gray-500">
                  This section will show {activeTab.toLowerCase()} as they are created.
                </p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  )
}

export default ActivitySection